
export default function RooftopSustainability() {
  const sustainabilityFeatures = [
    {
      icon: 'ri-sun-line',
      title: '태양광 발전',
      description: '옥상 태양광 패널로 건물 에너지의 30% 자체 생산'
    },
    {
      icon: 'ri-drop-line',
      title: '빗물 재활용',
      description: '빗물 집수 시스템으로 조경 용수 및 화장실 용수 활용'
    },
    {
      icon: 'ri-recycle-line',
      title: '폐기물 관리',
      description: 'AI 기반 스마트 분리수거 및 음식물 처리 시스템'
    },
    {
      icon: 'ri-temp-cold-line',
      title: '에너지 효율',
      description: '고효율 단열재 및 3중 로이유리로 에너지 손실 최소화'
    },
    {
      icon: 'ri-plant-line',
      title: '수직 정원',
      description: '건물 외벽 수직 정원으로 도심 속 녹지 공간 확대'
    },
    {
      icon: 'ri-leaf-line',
      title: '친환경 자재',
      description: '재활용 가능한 친환경 건축 자재 사용으로 탄소 배출 저감'
    }
  ];

  return (
    <section id="sustainability" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">옥상 정원 & 지속가능성</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            친환경 설비와 녹색 공간으로 지속가능한 미래를 만들어갑니다.<br />
            자연과 기술이 조화를 이루는 프리미엄 생활환경을 제공합니다.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/2d69efd01b6c10771db60d31d4533355.jpg" 
              alt="옥상 정원" 
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">8층 프리미엄 옥상 정원</h3>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              도심 속에서 자연을 만끽할 수 있는 프리미엄 옥상 정원입니다. 
              사계절 내내 아름다운 조경과 함께 서울의 스카이라인을 감상하며 
              여유로운 시간을 보낼 수 있습니다.
            </p>
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-plant-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <strong className="text-gray-900 block">사계절 정원</strong>
                  <p className="text-gray-600 text-sm">계절별 테마 조경과 다양한 식물</p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-run-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <strong className="text-gray-900 block">조깅 트랙</strong>
                  <p className="text-gray-600 text-sm">전망을 즐기며 운동할 수 있는 트랙</p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-goblet-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <strong className="text-gray-900 block">야외 라운지</strong>
                  <p className="text-gray-600 text-sm">BBQ 시설과 파티 공간</p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-focus-3-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <strong className="text-gray-900 block">명상 공간</strong>
                  <p className="text-gray-600 text-sm">조용히 휴식을 취할 수 있는 힐링 존</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">친환경 & 지속가능성</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sustainabilityFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all cursor-pointer group">
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-green-600 transition-colors">
                  <i className={`${feature.icon} text-3xl text-green-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-10">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-green-600 mb-2">30%</div>
              <div className="text-gray-700 font-medium">에너지 자체 생산</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-green-600 mb-2">50%</div>
              <div className="text-gray-700 font-medium">물 재활용률</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-green-600 mb-2">40%</div>
              <div className="text-gray-700 font-medium">탄소 배출 감소</div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <p className="text-lg text-gray-700">
              <strong className="text-green-600">친환경 인증</strong> 예정: 녹색건축 인증, LEED 골드 등급
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
